import { useState, type FormEvent } from 'react';
import type { JoinResponse } from '@gonzovnc/protocol';
import { GonzoApi, ApiError } from '../core/api.js';

interface Props {
  makeApi: () => GonzoApi;
  onJoined: (api: GonzoApi, join: JoinResponse) => void;
}

/** Deep link: https://…/j/XXXX-XXXX pre-fills the access code (shareable invite URL). */
function codeFromPath(): string {
  const m = /^\/j\/([A-Za-z0-9-]{4,24})$/.exec(window.location.pathname);
  return m?.[1]?.toUpperCase() ?? '';
}

export function JoinPage({ makeApi, onJoined }: Props) {
  const [code, setCode] = useState(codeFromPath());
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const api = makeApi();
      const join = await api.join(code.trim(), name.trim() || 'Guest');
      try { sessionStorage.setItem('gv.code', code.trim()); } catch { /* private mode */ }
      onJoined(api, join);
    } catch (e2) {
      if (e2 instanceof ApiError) {
        setErr(
          e2.code.startsWith('code_') ? 'That code is not valid, has expired, or was revoked.'
          : e2.code === 'room_full' ? 'This room is full.'
          : e2.code === 'host_offline' ? 'The host is offline. Try again shortly.'
          : e2.code === 'rate_limited' ? 'Too many attempts. Wait a minute and try again.'
          : 'Could not join. Please try again.',
        );
      } else {
        setErr('Network error. Check your connection.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="join-wrap">
      <form className="join-card" onSubmit={submit}>
        <h1>GonzoVNC</h1>
        <p className="sub">Enter your access code to join the room.</p>
        <div className="field">
          <label htmlFor="code">Access code</label>
          <input id="code" className="code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXX-XXXX" autoComplete="off" autoCapitalize="characters" required />
        </div>
        <div className="field">
          <label htmlFor="name">Your name</label>
          <input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex" maxLength={24} />
        </div>
        <button className="primary" type="submit" disabled={busy || code.trim().length < 4} style={{ width: '100%' }}>
          {busy ? 'Joining…' : 'Join room'}
        </button>
        <div className="err">{err}</div>
      </form>
    </div>
  );
}
